import {
  Center,
  HStack,
  Tag,
  TagLabel,
  Text,
  TextProps,
} from '@chakra-ui/react';
import React from 'react';

import {
  date as commonDate,
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  DeliverableContainerGQLType,
  DeliverableContainerOutputLenderViewGQLType,
  DeliverableContainerReceiptActionTypeGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { Icon, TagIcon } from 'packages/common-frontend/components/icons';

export type DetailedStatus =
  | 'notStarted'
  | 'overdue'
  | 'finishUploading' // dueSoon (finish uploading / inputs incomplete)
  | 'readyToGenerateReport' // inputsComplete (need to generate the report)
  | 'readyToSubmit' // reportGenerated (report has been generate, but not shared with lender)
  // these are all report statuses as well
  | 'submitted'
  | 'requestRemediation'
  | 'approved';

export const detailedStatusColor: { [key in DetailedStatus]: string } = {
  overdue: 'red',
  finishUploading: 'orange',
  notStarted: 'blue',

  // required files done
  readyToGenerateReport: 'orange',

  // receipts exist
  readyToSubmit: 'orange',
  submitted: 'blue',
  requestRemediation: 'orange',
  approved: 'green',
};

export const detailedStatusIcon: { [key in DetailedStatus]: string } = {
  overdue: 'calendar-clock',
  finishUploading: 'file-arrow-up',
  notStarted: 'calendar-clock',

  // required files done
  readyToGenerateReport: 'files',

  // receipts exist
  readyToSubmit: 'mailbox-flag-up',
  submitted: 'mailbox',
  requestRemediation: 'messages-question',
  approved: 'circle-check',
};

export const DetailedStatusBadge = ({ status }: { status: DetailedStatus }) => {
  const colorScheme = detailedStatusColor[status];
  return (
    <Tag
      variant="solid-muted"
      colorScheme={colorScheme}
      width="fit-content"
      flexShrink={0}
    >
      <TagIcon icon={detailedStatusIcon[status]} />
      <TagLabel>{commonUtils.toTitleCase(status)}</TagLabel>
    </Tag>
  );
};

export const DetailedStatusCell = ({ status }: { status: DetailedStatus }) => {
  return (
    <Tag
      variant="ghost"
      colorScheme={detailedStatusColor[status]}
      size="sm"
      flexShrink={0}
    >
      <TagIcon icon={detailedStatusIcon[status]} />
      <TagLabel>{commonUtils.toTitleCase(status)}</TagLabel>
    </Tag>
  );
};

export const calculateDetailedStatus = (
  container: DeliverableContainerGQLType,
): DetailedStatus => {
  const currentDate = commonDate.currentDateStr();
  const diff = commonDate.diffDays(container.dueDate, currentDate);

  if (container.mostRecentActionType) {
    switch (container.mostRecentActionType) {
      case 'approved':
        return 'approved';
      case 'requestRemediation':
        return 'requestRemediation';
      case 'submitted':
        return 'submitted';
      case 'generated':
        return 'readyToSubmit';
      default:
        finleyAssertUnreachable(container.mostRecentActionType);
    }
  }

  if (
    container.totalFinalArtifacts === -1 &&
    container.totalDependencies === -1
  ) {
    // even if the deliverable has not been started, we still want to mark it as overdue
    if (diff < 0) {
      return 'overdue';
    }
    return 'notStarted';
  }
  if (container.completedDependencies === container.totalDependencies) {
    return 'readyToGenerateReport';
  }
  if (diff < 0) {
    return 'overdue';
  }
  return 'finishUploading';
};

// 'readyToSubmit' and 'readyToGenerateReport' are folded into the 'dueSoon' status
export type Status =
  | Exclude<
      DetailedStatus,
      'readyToGenerateReport' | 'readyToSubmit' | 'finishUploading'
    >
  | 'dueSoon';

export const statusColor: { [key in Status]: string } = {
  overdue: 'red',
  dueSoon: 'orange',
  notStarted: 'blue',

  // receipts exist
  submitted: 'blue',
  requestRemediation: 'orange',
  approved: 'green',
};

export const statusIcon: { [key in Status]: string } = {
  overdue: 'calendar-clock',
  dueSoon: 'calendar-clock',
  notStarted: 'calendar-clock',

  // receipts exist
  submitted: 'mailbox',
  requestRemediation: 'messages-question',
  approved: 'circle-check',
};

export const StatusIcon = ({ status }: { status: Status }) => {
  return (
    <Icon icon={statusIcon[status]} color={`${statusColor[status]}.300`} />
  );
};

export const StatusCell = ({ status }: { status: Status }) => {
  return (
    <HStack alignItems="center">
      <Center paddingRight="5px">
        <StatusIcon status={status} />
      </Center>
      <Text>{commonUtils.toTitleCase(status)}</Text>
    </HStack>
  );
};

export const calculateStatus = (
  container: DeliverableContainerGQLType,
): Status => {
  const currentDate = commonDate.currentDateStr();
  const diff = commonDate.diffDays(container.dueDate, currentDate);

  if (container.mostRecentActionType) {
    switch (container.mostRecentActionType) {
      case 'approved':
        return 'approved';
      case 'requestRemediation':
        return 'requestRemediation';
      case 'submitted':
        return 'submitted';
      case 'generated': {
        if (diff < 0) {
          return 'overdue';
        }
        return 'dueSoon';
      }
      default:
        finleyAssertUnreachable(container.mostRecentActionType);
    }
  }

  if (diff < 0) {
    // even if the deliverable has not been started, we still want to mark it as overdue
    // that's why this check comes before the not started/due soon distinction
    return 'overdue';
  }

  if (
    container.totalFinalArtifacts === -1 &&
    container.totalDependencies === -1
  ) {
    return 'notStarted';
  }
  return 'dueSoon';
};

// _____________________________________ LENDER STATUSES _____________________________________

export type LenderReceiptStatus =
  | 'readyForReview'
  | 'approved'
  | 'remediationRequested';

export const calculateLenderReceiptStatus = (
  action: DeliverableContainerReceiptActionTypeGQLType,
): LenderReceiptStatus => {
  switch (action) {
    case 'generated':
      throw new Error(
        'Lenders should never see a receipt with most recent status "generated".',
      );
    case 'submitted':
      return 'readyForReview';
    case 'approved':
      return 'approved';
    case 'requestRemediation':
      return 'remediationRequested';
    default:
      return finleyAssertUnreachable(action);
  }
};

export const lenderReceiptStatusColor: {
  [key in LenderReceiptStatus]: string;
} = {
  readyForReview: 'orange',
  approved: 'green',
  remediationRequested: 'orange',
};

export const lenderReceiptStatusIcon: {
  [key in LenderReceiptStatus]: string;
} = {
  readyForReview: 'mailbox-flag-up',
  approved: 'circle-check',
  remediationRequested: 'messages-question',
};

export const LenderReceiptStatusBadge = ({
  mostRecentAction,
}: {
  mostRecentAction: DeliverableContainerReceiptActionTypeGQLType;
}) => {
  const status = calculateLenderReceiptStatus(mostRecentAction);
  return (
    <Tag
      colorScheme={lenderReceiptStatusColor[status]}
      variant="solid-muted"
      flexShrink={0}
    >
      <TagIcon icon={lenderReceiptStatusIcon[status]} />
      <TagLabel>{commonUtils.toTitleCase(status)}</TagLabel>
    </Tag>
  );
};

export type LenderStatus =
  | LenderReceiptStatus
  | 'readyForReview'
  | 'inProgress'
  | 'overdue';

export const calculateLenderContainerStatus = (
  container: DeliverableContainerOutputLenderViewGQLType,
): LenderStatus => {
  // if latest receipt exists
  if (container.mostRecentActionType) {
    switch (container.mostRecentActionType) {
      case 'submitted':
        return 'readyForReview';
      case 'approved':
        return 'approved';
      case 'requestRemediation':
        return 'remediationRequested';
      default:
        return finleyAssertUnreachable(container.mostRecentActionType);
    }
  }

  // if receipt doesn't exist, must be either overdue or inProgress
  const currentDate = commonDate.currentDateStr();
  const diff = commonDate.diffDays(container.dueDate, currentDate);
  if (diff < 0) {
    return 'overdue';
  }

  return 'inProgress';
};

export const lenderContainerStatusColor: { [key in LenderStatus]: string } = {
  readyForReview: 'orange',
  approved: 'green',
  remediationRequested: 'orange',
  inProgress: 'orange',
  overdue: 'red',
};

export const lenderContainerStatusIcon: { [key in LenderStatus]: string } = {
  readyForReview: 'mailbox-flag-up',
  approved: 'circle-check',
  remediationRequested: 'messages-question',
  inProgress: 'spinner-third',
  overdue: 'calendar-clock',
};

export const LenderStatusCell = ({
  status,
  ...textProps
}: { status: LenderStatus } & TextProps) => {
  const iconColor = `${lenderContainerStatusColor[status]}.300`;
  const icon = lenderContainerStatusIcon[status];

  return (
    <HStack alignItems="center">
      <Center paddingRight="5px">
        <Icon icon={icon} color={iconColor} />
      </Center>
      <Text color="inherit" fontSize="sm" fontWeight="semibold" {...textProps}>
        {commonUtils.toTitleCase(status)}
      </Text>
    </HStack>
  );
};
